'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ArrowLeft, CheckCircle, Loader2, ShieldCheck, Clock } from 'lucide-react';
import type { DeliveryFormData } from './BookDeliveryWizard';

const PAYMENT_METHODS = [
  {
    id: 'Cash',
    label: 'Pesa Taslimu',
    sublabel: 'Cash on Delivery',
    desc: 'Lipa kwa pesa taslimu wakati delivery inapofikia',
    color: 'bg-green-50 border-green-200',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-700',
    icon: '💵',
    badge: null,
  },
  {
    id: 'M-Pesa',
    label: 'M-Pesa',
    sublabel: 'Vodacom M-Pesa',
    desc: 'Lipa kwa M-Pesa — utapokea maelekezo baada ya kuthibitisha',
    color: 'bg-red-50 border-red-200',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-700',
    icon: '📱',
    badge: 'Maarufu',
  },
  {
    id: 'Airtel Money',
    label: 'Airtel Money',
    sublabel: 'Airtel Tanzania',
    desc: 'Lipa kwa Airtel Money moja kwa moja',
    color: 'bg-orange-50 border-orange-200',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-700',
    icon: '📲',
    badge: null,
  },
  {
    id: 'Tigo Pesa',
    label: 'Tigo Pesa',
    sublabel: 'Tigo Tanzania',
    desc: 'Lipa kwa Tigo Pesa kwa haraka na usalama',
    color: 'bg-blue-50 border-blue-200',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-700',
    icon: '💳',
    badge: null,
  },
];

interface StepPaymentProps {
  formData: DeliveryFormData;
  updateForm: (partial: Partial<DeliveryFormData>) => void;
  onBack: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
}

interface PaymentFormValues {
  paymentMethod: string;
  mobileNumber: string;
  agreeTerms: boolean;
}

export default function StepPayment({ formData, updateForm, onBack, onConfirm, isSubmitting: externalSubmitting }: StepPaymentProps) {
  const [submitting, setSubmitting] = useState(false);
  const isProcessing = submitting || !!externalSubmitting;
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<PaymentFormValues>({
    defaultValues: {
      paymentMethod: formData.paymentMethod,
      mobileNumber: '',
      agreeTerms: false,
    },
  });

  const selectedMethod = watch('paymentMethod');
  const needsMobileNumber = ['M-Pesa', 'Airtel Money', 'Tigo Pesa'].includes(selectedMethod);

  const onSubmit = async (data: PaymentFormValues) => {
    setSubmitting(true);
    updateForm({ paymentMethod: data.paymentMethod });
    setSubmitting(false);
    onConfirm();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {/* Order Total Reminder */}
      <div className="card-elevated p-4 gradient-primary text-white rounded-xl">
        <p className="text-sm font-medium opacity-90 mb-1">Jumla ya Kulipa</p>
        <p className="text-3xl font-extrabold tsh-value">TSh {formData.estimatedPrice.toLocaleString()}</p>
        <p className="text-xs opacity-75 mt-1">{formData.pickupAddress} → {formData.deliveryAddress}</p>
      </div>

      {/* Pending Payment Notice */}
      <div className="flex items-start gap-3 p-3.5 rounded-xl bg-amber-50 border border-amber-200">
        <Clock size={16} className="text-amber-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-xs font-semibold text-amber-800">Malipo Yatasubiri Uthibitisho</p>
          <p className="text-xs text-amber-700 mt-0.5">
            Order itahifadhiwa na hali ya <span className="font-semibold">malipo yanayosubiri</span>. Utapata maelekezo ya kulipa baada ya rider kupewa order yako.
          </p>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="card-elevated p-5">
        <h3 className="text-base font-semibold text-foreground mb-1">Chagua Njia ya Malipo</h3>
        <p className="text-xs text-muted-foreground mb-4">Chagua jinsi unavyotaka kulipa delivery yako</p>

        <input type="hidden" {...register('paymentMethod', { required: 'Tafadhali chagua njia ya malipo' })} />

        <div className="space-y-2.5">
          {PAYMENT_METHODS.map(method => (
            <button
              key={`pay-${method.id}`}
              type="button"
              onClick={() => setValue('paymentMethod', method.id, { shouldValidate: true })}
              className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all duration-150 ${
                selectedMethod === method.id
                  ? 'border-primary bg-primary/5 shadow-sm'
                  : 'border-border bg-card hover:border-primary/30'
              }`}
            >
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl ${method.iconBg} shrink-0`}>
                {method.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-foreground">{method.label}</p>
                  {method.badge && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary leading-none">
                      {method.badge}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{method.sublabel}</p>
                <p className="text-[11px] text-muted-foreground/80 mt-0.5 leading-snug">{method.desc}</p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                selectedMethod === method.id ? 'border-primary bg-primary' : 'border-border'
              }`}>
                {selectedMethod === method.id && (
                  <div className="w-2 h-2 bg-white rounded-full" />
                )}
              </div>
            </button>
          ))}
        </div>
        {errors.paymentMethod && <p className="text-xs text-red-600 mt-2">{errors.paymentMethod.message}</p>}
      </div>

      {/* Mobile Number Input (informational, not required for gateway) */}
      {needsMobileNumber && (
        <div className="card-elevated p-4 animate-slide-up">
          <label className="block text-xs font-semibold text-foreground mb-1 tracking-wide">
            Nambari ya {selectedMethod} <span className="text-muted-foreground font-normal">(hiari)</span>
          </label>
          <p className="text-[11px] text-muted-foreground mb-2">Ingiza nambari utakayolipa nayo — itahifadhiwa kwa ajili ya malipo</p>
          <input
            {...register('mobileNumber', {
              pattern: { value: /^\+?255\s?\d{9}$|^0\d{9}$/, message: 'Nambari si sahihi' }
            })}
            placeholder="+255 754 000 000"
            className={`input-base w-full px-4 py-2.5 text-sm ${errors.mobileNumber ? 'input-error' : ''}`}
          />
          {errors.mobileNumber && <p className="text-xs text-red-600 mt-1">{errors.mobileNumber.message}</p>}
          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
            <ShieldCheck size={12} className="text-green-600" />
            Nambari yako itahifadhiwa salama na kutumika kwa malipo haya tu
          </p>
        </div>
      )}

      {/* Terms */}
      <div className="card-elevated p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            {...register('agreeTerms', { required: 'Lazima ukubali masharti' })}
            className="mt-0.5 w-4 h-4 accent-primary"
          />
          <div>
            <p className="text-sm text-foreground">
              Nakubali <span className="text-primary font-semibold cursor-pointer hover:underline">Masharti ya Huduma</span> na{' '}
              <span className="text-primary font-semibold cursor-pointer hover:underline">Sera ya Faragha</span> ya Percel Kitaa
            </p>
            <p className="text-xs text-muted-foreground mt-1">Bei iliyoonyeshwa ni ya kukadiriwa. Bei ya mwisho itategemea umbali halisi.</p>
          </div>
        </label>
        {errors.agreeTerms && <p className="text-xs text-red-600 mt-1">{errors.agreeTerms.message}</p>}
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={isProcessing}
          className="flex-1 btn-secondary py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <ArrowLeft size={16} />
          Rudi
        </button>
        <button
          type="submit"
          disabled={isProcessing}
          className="flex-1 btn-primary py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-70"
          style={{ minWidth: '160px' }}
        >
          {isProcessing ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Inatuma...
            </>
          ) : (
            <>
              <CheckCircle size={16} />
              Thibitisha Order
            </>
          )}
        </button>
      </div>
    </form>
  );
}